import { TreeDataItem, TreeView } from "@/components/tree-view";
import { Skeleton } from "@/components/ui/skeleton";
import type {
  GithubProjectEntity,
  GithubRepoEntity,
} from "@cohesive/api-entities";
import { debounce } from "lodash";
import { FolderGit2, Kanban } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";
import { ViewHeader } from "./components";
import { useGitHubIssuesSearch } from "./useGitHubIssuesSearch";
import { useGitHubProjectItems } from "./useGitHubProjectItems";
import { useGitHubProjectsList } from "./useGitHubProjectsList";
import { useGitHubPRsSearch } from "./useGitHubPRsSearch";
import { useGitHubReposList } from "./useGitHubReposList";
import {
  ProjectItemsView,
  ProjectsListView,
  RepoItemsView,
  ReposListView,
} from "./views";

type ViewMode = "repos-list" | "projects-list" | "repo-items" | "project-items";
type ItemType = "issues" | "prs";
type IssueState = "all" | "open" | "closed";

export const GitHubSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("projects-list");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Navigation state
  const [selectedRepo, setSelectedRepo] = useState<GithubRepoEntity | null>(
    null,
  );
  const [selectedProject, setSelectedProject] =
    useState<GithubProjectEntity | null>(null);

  // Filters
  const [itemType, setItemType] = useState<ItemType>("issues");
  const [issueState, setIssueState] = useState<IssueState>("all");

  // Ref for auto-loading when content doesn't overflow
  const resultsRef = useRef<HTMLDivElement>(null);

  // Debounced search
  const debouncedSetSearch = useMemo(
    () => debounce((query: string) => setDebouncedQuery(query), 300),
    [],
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSetSearch(value);
  };

  // Fetch all repos and projects for sidebar
  const {
    data: reposData,
    isLoading: isReposLoading,
    genericById: reposGenericById,
  } = useGitHubReposList(connection);
  const repos = reposData?.data || [];

  const { data: projectsData, isLoading: isProjectsLoading } =
    useGitHubProjectsList({
      connection,
      owner: "venchrz",
      ownerType: "organization",
    });
  const projects = projectsData?.data || [];

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed entity. Single-response hooks carry them in
  // data_generic; the infinite hooks expose a prebuilt map.
  const projectsGenericById = useMemo(
    () => buildGenericById(toGenericArray(projectsData?.data_generic)),
    [projectsData],
  );

  // Build search queries for repo items
  const issuesQuery = useMemo(() => {
    if (!selectedRepo) return "";
    const parts: string[] = ["is:issue", `repo:${selectedRepo.data.full_name}`];
    if (issueState !== "all") parts.push(`state:${issueState}`);
    if (debouncedQuery) parts.push(debouncedQuery);
    parts.push("sort:updated-desc");
    return parts.join(" ");
  }, [selectedRepo, issueState, debouncedQuery]);

  const prsQuery = useMemo(() => {
    if (!selectedRepo) return "";
    const parts: string[] = ["is:pr", `repo:${selectedRepo.data.full_name}`];
    if (issueState !== "all") parts.push(`state:${issueState}`);
    if (debouncedQuery) parts.push(debouncedQuery);
    parts.push("sort:updated-desc");
    return parts.join(" ");
  }, [selectedRepo, issueState, debouncedQuery]);

  // Fetch repo items (issues or PRs)
  const { data: issuesData, isLoading: isIssuesLoading } =
    useGitHubIssuesSearch({
      connection,
      query: issuesQuery,
      enabled:
        viewMode === "repo-items" && itemType === "issues" && !!selectedRepo,
    });

  const { data: prsData, isLoading: isPRsLoading } = useGitHubPRsSearch({
    connection,
    query: prsQuery,
    enabled: viewMode === "repo-items" && itemType === "prs" && !!selectedRepo,
  });

  const issuesGenericById = useMemo(
    () => buildGenericById(toGenericArray(issuesData?.data_generic)),
    [issuesData],
  );

  const prsGenericById = useMemo(
    () => buildGenericById(toGenericArray(prsData?.data_generic)),
    [prsData],
  );

  // Fetch project items with infinite scroll (fetch ALL, filter client-side)
  const {
    data: projectItemsData,
    isLoading: isProjectItemsLoading,
    fetchNextPage: fetchNextProjectItems,
    hasNextPage: hasNextProjectItems,
    isFetchingNextPage: isFetchingNextProjectItems,
    genericById: projectItemsGenericById,
  } = useGitHubProjectItems({
    connection,
    projectId: selectedProject?.data.id || "",
    enabled: viewMode === "project-items" && !!selectedProject,
  });

  // Navigation handlers
  const handleRepoSelect = (repo: GithubRepoEntity) => {
    setSelectedRepo(repo);
    setViewMode("repo-items");
    setSearchQuery("");
    setDebouncedQuery("");
  };

  const handleProjectSelect = (project: GithubProjectEntity) => {
    setSelectedProject(project);
    setViewMode("project-items");
    setSearchQuery("");
    setDebouncedQuery("");
  };

  // Handle scroll for project items infinite loading
  const handleProjectItemsScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        hasNextProjectItems &&
        !isFetchingNextProjectItems
      ) {
        fetchNextProjectItems();
      }
    },
    [hasNextProjectItems, isFetchingNextProjectItems, fetchNextProjectItems],
  );

  // Auto-load more pages if content doesn't overflow (for filtered results)
  useEffect(() => {
    if (
      viewMode === "project-items" &&
      !isProjectItemsLoading &&
      !isFetchingNextProjectItems &&
      hasNextProjectItems &&
      resultsRef.current
    ) {
      const { scrollHeight, clientHeight } = resultsRef.current;
      // If content doesn't overflow, fetch more
      if (scrollHeight <= clientHeight) {
        fetchNextProjectItems();
      }
    }
  }, [
    viewMode,
    projectItemsData,
    isProjectItemsLoading,
    isFetchingNextProjectItems,
    hasNextProjectItems,
    fetchNextProjectItems,
    issueState, // Re-check when filter changes
  ]);

  // Handle back navigation
  const handleBack = () => {
    if (viewMode === "repo-items") {
      setViewMode("repos-list");
      setSelectedRepo(null);
      setItemType("issues");
      setIssueState("all");
      setSearchQuery("");
      setDebouncedQuery("");
    } else if (viewMode === "project-items") {
      setViewMode("projects-list");
      setSelectedProject(null);
      setSearchQuery("");
      setDebouncedQuery("");
    }
  };

  // Filter repos and projects by search
  const filteredRepos = useMemo(() => {
    if (!debouncedQuery) return repos;
    const query = debouncedQuery.toLowerCase();
    return repos.filter(
      (repo) =>
        repo.data.full_name.toLowerCase().includes(query) ||
        repo.data.description?.toLowerCase().includes(query),
    );
  }, [repos, debouncedQuery]);

  const filteredProjects = useMemo(() => {
    if (!debouncedQuery) return projects;
    const query = debouncedQuery.toLowerCase();
    return projects.filter(
      (project) =>
        project.data.title.toLowerCase().includes(query) ||
        project.data.description?.toLowerCase().includes(query),
    );
  }, [projects, debouncedQuery]);

  // Build sidebar tree
  const sidebarTree: TreeDataItem[] = useMemo(() => {
    return [
      {
        id: "projects",
        name: "Projects",
        icon: Kanban,
        onClick: () => {
          if (viewMode !== "projects-list") {
            setViewMode("projects-list");
            setSelectedProject(null);
            setSearchQuery("");
            setDebouncedQuery("");
          }
        },
      },
      {
        id: "repos",
        name: "Repositories",
        icon: FolderGit2,
        onClick: () => {
          if (viewMode !== "repos-list") {
            setViewMode("repos-list");
            setSelectedRepo(null);
            setSearchQuery("");
            setDebouncedQuery("");
          }
        },
      },
    ];
  }, [viewMode]);

  // Render main content based on view mode
  const renderContent = () => {
    if (viewMode === "repos-list") {
      return (
        <ReposListView
          repos={filteredRepos}
          isLoading={isReposLoading}
          onRepoSelect={handleRepoSelect}
          onAddToContext={onSelect}
          genericById={reposGenericById}
        />
      );
    }

    if (viewMode === "projects-list") {
      return (
        <ProjectsListView
          projects={filteredProjects}
          isLoading={isProjectsLoading}
          onProjectSelect={handleProjectSelect}
          onAddToContext={onSelect}
          genericById={projectsGenericById}
        />
      );
    }

    if (viewMode === "repo-items") {
      const items =
        itemType === "issues" ? issuesData?.data || [] : prsData?.data || [];
      const isLoading = itemType === "issues" ? isIssuesLoading : isPRsLoading;
      const genericById =
        itemType === "issues" ? issuesGenericById : prsGenericById;

      return (
        <RepoItemsView
          items={items}
          itemType={itemType}
          isLoading={isLoading}
          onItemSelect={onSelect}
          genericById={genericById}
        />
      );
    }

    if (viewMode === "project-items") {
      return (
        <ProjectItemsView
          items={projectItemsData?.data || []}
          isLoading={isProjectItemsLoading}
          issueState={issueState}
          searchQuery={debouncedQuery}
          onItemSelect={onSelect}
          isFetchingNextPage={isFetchingNextProjectItems}
          genericById={projectItemsGenericById}
        />
      );
    }

    return null;
  };

  // Get placeholder text
  const getPlaceholder = () => {
    if (viewMode === "repos-list") return "Search repositories...";
    if (viewMode === "projects-list") return "Search projects...";
    if (viewMode === "repo-items") {
      return `Search ${itemType === "issues" ? "issues" : "pull requests"} in ${selectedRepo?.data.full_name}...`;
    }
    if (viewMode === "project-items") {
      return `Search items in ${selectedProject?.data.title}...`;
    }
    return "Search...";
  };

  return (
    <Selector>
      <SelectorSidebar>
        {isReposLoading || isProjectsLoading ? (
          <div className="p-2 space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <TreeView data={sidebarTree} expandAll={false} />
        )}
      </SelectorSidebar>

      <SelectorSearch
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={getPlaceholder()}
      />

      <SelectorResults
        ref={resultsRef}
        style={{ paddingBottom: "200px" }}
        onScroll={
          viewMode === "project-items" ? handleProjectItemsScroll : undefined
        }
      >
        <ViewHeader
          selectedRepo={selectedRepo}
          selectedProject={selectedProject}
          onBack={handleBack}
          itemType={viewMode === "repo-items" ? itemType : undefined}
          onItemTypeChange={viewMode === "repo-items" ? setItemType : undefined}
          issueState={
            viewMode === "repo-items" || viewMode === "project-items"
              ? issueState
              : undefined
          }
          onIssueStateChange={
            viewMode === "repo-items" || viewMode === "project-items"
              ? setIssueState
              : undefined
          }
        />

        {renderContent()}
      </SelectorResults>
    </Selector>
  );
};
